import { iconList } from "@/assets/icons";
import placeHolderImage from "@/assets/images/placeholder-img.png";
import { Button } from "@/components/atoms/button";
import { Link } from "@/components/atoms/link";
import { StarRating } from "@/components/atoms/star-rating";
import { cn, localCloudflareLoader } from "@/lib/utils";
import { ComponentJapanWeather, UploadFile } from "@/types/generated";
import { CircleChevronRight } from "lucide-react";
import { useLocale } from "next-intl";
import Image from "next/image";
import { HotelServiceTag } from "./HotelServiceTag";
import styles from "./styles.module.css";

interface IHotelItemProps {
  image?: UploadFile;
  name: string;
  rating: number;
  reviewCount: string;
  price: string;
  city: string;
  showMoreBtnText?: string | null;
  fromLabel?: string | null;
  brandImageUrl?: string;
  serviceIcons:
    | {
        item_name?: string;
        title?: string | null;
        documentId?: string;
        side_popup_icon?: string | null;
        order?: number | null;
      }[]
    | null;
  onClick?: () => void;
  className?: string;
  slug?: string | null;
  externalUrl?: string | null;
  weather?: ComponentJapanWeather;
  priority?: boolean;
}
/**
 * HotelItem Component
 * Renders a hotel card with image, details, weather info, and navigation options.
 * Used in hotel listing or search result views.
 */
function formatReviewCount(count: string | number | undefined) {
  const num = Number(count) || 0; //convert to number
  return num.toLocaleString("ja-JP"); // format locale
}
export default function HotelItem(props: Readonly<IHotelItemProps>) {
  const {
    image,
    name,
    rating,
    price,
    city,
    reviewCount,
    showMoreBtnText = "View more",
    brandImageUrl,
    serviceIcons,
    fromLabel,
    className,
    slug,
    onClick,
    externalUrl,
    weather,
    priority = false,
  } = props;
  const { Map: MapIcon } = iconList;
  const locale = useLocale();

  // Mapping for weather condition labels in Japanese
  const wheatherLabel = {
    cloudy: "曇り",
    flurries: "霰",
    mostlycloudy: "曇り",
    partlycloudy: "所により曇り",
    rain: "雨",
    sleet: "雨",
    snow: "雪",
    sunny: "晴れ",
    tstorms: "雷雨",
  };

  // Dynamically resolve weather icon class based on weather condition
  const getWeatherIconClass = (icon?: string | null) => {
    if (!icon) return styles.clear;
    return styles[icon.toLowerCase()] || styles.clear;
  };

  // Refactor navigateLink assignment
  let navigateLink = "";
  if (externalUrl) {
    navigateLink = externalUrl;
  } else if (slug) {
    navigateLink = `/${locale}/${slug}`;
  }

  const onNavigate = () => {
    globalThis.location.href = navigateLink;
  };

  // Format review count into Japanese locale

  // log cloudflare image url for debugging with priority flag

  return (
    <div
      className={cn(
        "flex h-full flex-col overflow-hidden rounded-lg",
        "border border-(--stroke-gray-light) bg-white shadow-md",
        "transition-shadow hover:border-(--stroke-gray-normal)",
        className,
      )}
    >
      {/* Hotel image section with weather overlay */}
      <button
        type="button"
        onClick={onNavigate}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onNavigate();
        }}
        className="relative h-[209px] w-full shrink-0 cursor-pointer border-none bg-transparent p-0"
        aria-label={name}
        tabIndex={0}
        style={{ outline: "none" }}
      >
        <Image
          objectFit="cover"
          src={image?.cloudflare_url || image?.url || placeHolderImage}
          alt={image?.alternativeText || ""}
          aria-label={image?.caption || ""}
          fill
          priority={priority}
          loader={localCloudflareLoader(480)}
        />

        {weather && (
          <div
            className={`${styles.weather} absolute bottom-2 left-2 flex items-center justify-center gap-1 rounded-full bg-black px-3 py-1 text-[11px] text-white opacity-70`}
          >
            <i
              className={`${getWeatherIconClass(weather?.icon_weather)} text-sm`}
            />
            <span className="text-xs">
              {weather?.temperature_c}℃{" "}
              {
                wheatherLabel[
                  weather?.icon_weather as keyof typeof wheatherLabel
                ]
              }
            </span>
          </div>
        )}
      </button>
      <button
        type="button"
        onClick={onNavigate}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onNavigate();
        }}
        className="flex h-full min-h-[302px] cursor-pointer flex-col border-none bg-transparent p-4 pt-2 md:min-h-[275px]"
        aria-label={name}
        tabIndex={0}
        style={{ outline: "none" }}
      >
        <div className="flex h-10 items-center justify-between">
          <div className="flex gap-1">
            <MapIcon />
            <span className="text-xs text-[#7E849A]">{city}</span>
          </div>
          {brandImageUrl && (
            <div className="relative h-[45px] w-[120px]">
              <Image
                src={brandImageUrl}
                alt="brand"
                layout="fill"
                className="object-contain"
              />
            </div>
          )}
        </div>
        <h3 className="text-primary mt-1 mb-1 line-clamp-2 cursor-pointer text-left leading-tight font-(--font-bold)">
          {name}
        </h3>
        {rating > 0 && (
          <div className="mt-3 flex">
            <div className="bg-primary-950 relative -top-[5px] mr-2 rounded-xs px-1 py-0.5 text-xs font-semibold text-white">
              {rating?.toFixed(1)}
              <div className="absolute -bottom-1 left-1/2 h-0 w-0 -translate-x-1/2 border-t-4 border-r-4 border-l-4 border-t-blue-900 border-r-transparent border-l-transparent"></div>
            </div>
            <StarRating value={rating} />
            <span className="text-xs text-gray-600">
              {formatReviewCount(reviewCount) || 0} 件のクチコミ
            </span>
          </div>
        )}
        <div className="flex-1 py-2">
          <HotelServiceTag serviceIcons={serviceIcons} />
        </div>
        <span className="text-primary-950 text-left text-xl font-(--font-semibold)">
          {fromLabel}
          {price}
        </span>
      </button>
      {onClick ? (
        <div className="p-4 pt-0">
          <Button
            onClick={onClick}
            className="min-h-9 w-full justify-between! rounded-sm bg-(--fill-secondary-solid-normal) py-5 text-sm font-(--font-medium) text-(--text-base-primary) hover:bg-(--fill-secondary-solid-lighter) active:bg-(--fill-secondary-solid-stronger) md:min-h-10 md:justify-center md:text-base"
          >
            {showMoreBtnText}
            <CircleChevronRight strokeWidth="1px" className="ml-10 h-4 w-4" />
          </Button>
        </div>
      ) : (
           <div className=" p-4 pt-0">
          <Button
            asChild
            className="min-h-9 w-full justify-between! rounded-sm py-5 text-sm font-(--font-medium) md:min-h-10 md:justify-center md:text-base"
          >
            <Link href={navigateLink} target="_parent">
              {showMoreBtnText}
              <CircleChevronRight strokeWidth="1px" className="ml-10 h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
